import { Stack, useTheme } from "@mui/material";
import { Fragment, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import {
  Outlet,
  //useLoaderData,
  useLocation,
  useNavigate,
  useSearchParams
} from "react-router-dom";
// import IntroVideo from "../../assets/OL_login_spalsh.mp4";
import {
  Footer,
  Header,
  LoginSpinner,
  Modal,
  Navbar,
  SessionRevoker
} from "../../components";
import { SkipContentAccessibility } from "../../components/Accessibility/SkipContent.component";
import { MultiTabBlocker } from "../../components/MultiTabBlocker";
import { OverlaySupport } from "../../components/OverlaySupportButtons/OverlaySupport.component";
import TutorialBox from "../../components/TutorialBox/TutorialBox.component";
import {
  AUTH_MODE
  //NAVBAR_ITEMS
} from "../../consts";
import {
  useCommunityTrackPathname,
  useCommunityTrackVisibility,
  useContactsWidget,
  useElementHeightRem,
  useWindowSize
} from "../../hooks";
import { capitalizeString, useGetLanguages, useGetTranslations } from "../../queries";
import {
  useAuthStore,
  useLanguageStore,
  useNavbarStore
} from "../../stores";
import type { InitiativeOptions } from "../../types";
import { useTabManager } from "../../useTabManager";
import {
  addTimeToCurrentDate,
  getCookieByName,
  getEnvironmentVariables,
  getLastPath, getPageTitle
} from "../../utils/general";
import { hasValidFromUrl } from "../../utils/login";

/// cfr. https://reactrouter.com/en/main/hooks/use-outlet-context
export type OutletContext = {
  sectionUrl: string,
  showTutorialWidget: boolean,
  setShowTutorialWidget: (item: boolean) => void
}

const { environment, analytics, favicon } = getEnvironmentVariables();

/*
interface UserMNG {
  id: number,
  name: string,
  self_signup_enabled: boolean,
  splash: boolean,
  tutorial: boolean,
  tutorial_forced: boolean,
  maintenance_page_global: boolean
}
*/
const getInitiativeIdFromUrl = (fromUrl: string | null, initiatives: InitiativeOptions[])=>{
  if(!fromUrl) return null;
  const initiative = initiatives.find(
    (initiative) =>
      initiative.urlDomain &&
      initiative.urlCorporateDomain &&
      fromUrl?.toLowerCase().includes(`${initiative.urlDomain}.${initiative.urlCorporateDomain}/`.toLowerCase())
  );
  return initiative ? initiative.id : null;
};

export function Root() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    pathname,
    state: routerState
  } = useLocation();
  const theme = useTheme();

  const [showTutorialWidget, setShowTutorialWidget] = useState(false);
  const [showTutorialUserMenu, setShowTutorialUserMenu] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const setSpinnerLoginVisible = useAuthStore(state => state.setSpinnerLoginVisible);
  const spinnerLoginVisible = useAuthStore(state => state.spinnerLoginVisible);
  const agreedToConditions = useAuthStore(state => Boolean(state.userInfo?.privacy_consent_flag));
  const authError = useAuthStore(state => state.error);
  const authNotice = useAuthStore(state => state.notice);
  const corporateHasOnBoarding = useAuthStore(state => Boolean(state.corporateInfo?.onboarding));
  const completedOnBoarding = useAuthStore(state => Boolean(state.userInfo?.onboarding_flag));
  const isAuthenticated = useAuthStore(state => Boolean(state.userData && state.userInfo));

  const initiatives = useAuthStore(state => state.userInfo?.initiatives || []);
  // const isMoreAuthenticated = useAuthStore(state => Boolean(state.userData?.initiative_id && state.userInfo));
  const hasInitiative = useAuthStore(state => Boolean(state.userData?.initiative_id));
  const navigateTo = useAuthStore(state => state.navigateTo);
  const userRoles = useAuthStore(state => state.userData?.roles);
  const resetError = useAuthStore(state => state.resetError);
  const resetNavigate = useAuthStore(state => state.resetNavigate);
  const resetNotice = useAuthStore(state => state.resetNotice);
  const seedLogout = useAuthStore(state => state.seedLogout);
  const seedSession = useAuthStore(state => state.seedSession);
  const setSessionSeeding = useAuthStore(state => state.setSessionSeeding);
  const sessionToken = useAuthStore(state => state.sessionToken);
  const switchToInitiative = useAuthStore(state => state.switchToInitiative);
  const smartConfiguration = useAuthStore(state => state.smartConfiguration);
  //const navbarItem = useNavbarStore(state => state.item);
  const setNavbarItem = useNavbarStore(state => state.setNavbarItem);
  //const setIsLoading = useAuthStore(state => state.setIsLoading);

  // const sectionUrl = (navbarItem === NAVBAR_ITEMS.FOR_YOU) ? "per-te" : "esplora";
  //const pathFirstSegment = pathname.split("/")[1] ?? "";
  let sectionUrl = pathname === "/per-te" ? "per-te" :"esplora";

  const isPO = userRoles?.includes("PRODUCT_OWNER");
  const qrCodeValidate = useAuthStore(state=>state.qrCodeValidate);
  const resetQrValidation = useAuthStore(state=> state.resetQrValidation);
  const { language, languageCookie, setLanguage } = useLanguageStore(state => state);
  const fromUrl = useAuthStore(state => state.fromUrl);
  const hasSeedSession = useAuthStore(state => state.hasSeedSession);
  const registrationCompletedVisible = useAuthStore(state => state.registrationCompletedVisible);
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const corporateInfo = useAuthStore(state => state.corporateInfo);
  const { isActive, activateTab } = useTabManager();

  // Se corporateInfo è assente o incompleta, forza il logout per ripulire lo stato
  useEffect(() => {
    const state = useAuthStore.getState();
    if (
      state.session &&
      state.sessionToken &&
      state.userData &&
      state.userData.organization_id &&
      (!state.corporateInfo ||
        (state.corporateInfo && state.corporateInfo.enable_community_flag === undefined))
    ) {

      //TODO rimuovere tra qualche mese magari
      // eslint-disable-next-line no-console
      console.warn("corporateInfo assente o incompleta, forzo il logout");
      useAuthStore.getState().signOut("/access/login");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pageTitle = getPageTitle(pathname);

  const [footerRef, footerHeightRem] = useElementHeightRem() as [React.RefObject<HTMLDivElement>, number];
  // const splash =  useNavbarStore(state => state.splash);
  // const setSplash = useNavbarStore(state => state.setSplash);

  useCommunityTrackPathname();
  useCommunityTrackVisibility();
  useContactsWidget(hasInitiative && smartConfiguration?.freshDesk == true && !smartConfiguration?.showMaintenancePage, language?.id || null);
  // eslint-disable-next-line max-len

  const { width: windowWidth } = useWindowSize();

  const excessWidth = (windowWidth - 1440) + 22; // 22 is a margin value set by the widget script

  const authMode = searchParams.get("mode") as keyof typeof AUTH_MODE;

  // query param for seeding session
  const seedSessionId = searchParams.get("seed_session");

  // query params for setting logout state
  const errorParams = searchParams.get("error");
  const navigateParam = searchParams.get("navigate");
  const codeFromIdp = searchParams.get("code");

  const mainPageNavItem = smartConfiguration?.visForyou === false ? "EXPLORE" : "FOR_YOU";

  if (seedSessionId) {
    setSessionSeeding(true);  //TODO:da eliminare??? non penso perchè potrei atterare direttamente con un seed session, esempio quando entrimo da local
    document.cookie =
      `secure; expires=${addTimeToCurrentDate(5)}; domain=.${getLastPath()}; path=/`;
  }

  //const userMgmtData = useLoaderData() as UserMNG;

  // const [showSplash, setShowSplash] = useState(false);

  // useEffect(() => {
  //   if (!userMgmtData?.splash) return;
  //   if(!routerState?.maintenancePage && !splash) {
  //     setShowSplash(!!userMgmtData?.splash && (mainPage === pathname));
  //     setTimeout(() => {
  //       setShowSplash(false);
  //       setSplash(true);
  //     }, 4000);
  //   }
  // }, [userMgmtData?.splash, smartConfiguration?.urlDomain, mainPage]);

  const shouldRedirectToInitiative = () => {
    return (smartConfiguration?.urlCorporateDomain && smartConfiguration.urlCorporateDomain)
    && (`https://${smartConfiguration.urlDomain}.${smartConfiguration.urlCorporateDomain}` !== window.location.origin)
    && (environment !== "LOCAL") && agreedToConditions;
  };

  const shouldShowAuthError = () => {
    return authError && pathname === "/access/login";
  };

  const shouldHandleNavigateTo = () => {
    return navigateTo;
  };

  const handleNavigateTo = () => {
    const target = navigateTo!.target;
    const path = target.startsWith("/") ? target : new URL(target).pathname;
    // mantengo il query param loginType se presente
    let finalPath = path;

    if (pathname === finalPath.split("?")[0]) {
      resetNavigate();
      return;
    }else{
      const loginType = searchParams.get("loginType");
      if (loginType) {
        finalPath += finalPath.includes("?") ? `&loginType=${loginType}` : `?loginType=${loginType}`;
      }

      if (pathname === finalPath.split("?")[0]) {
        resetNavigate();
        return;
      }
      navigate(finalPath, { state: navigateTo!.state });
      setNavbarItem(mainPageNavItem);
    }
  };

  const shouldSeedSession = () => {
    return seedSessionId && authMode;
  };

  const handleSeedSession = () => {
    setSpinnerLoginVisible(true);
    searchParams.delete("mode");
    searchParams.delete("seed_session");
    searchParams.delete("switchToInitiative");
    setSearchParams(searchParams);
    seedSession(authMode, seedSessionId!);
  };

  const shouldHandleErrorParams = () => {
    return errorParams && authError === null;
  };

  const handleErrorParams = () => {
    seedLogout(errorParams, null);
    sessionStorage.setItem("disable_sso_redirect", "enabled");
    navigate("/sso-redirect-error");
  };

  const shouldHandleNavigateParam = () => {
    return navigateParam;
  };

  const handleNavigateParam = () => {
    searchParams.delete("navigate");
    setSearchParams(searchParams);
    navigate(navigateParam!);
  };

  const shouldGoToLogin = () => {
    return (spinnerLoginVisible &&
       pathname === "/" &&
        !isAuthenticated &&
        codeFromIdp === null &&
         !hasSeedSession &&
         !routerState?.recover === true &&
         !routerState?.firstAccess === true
    ) || (!spinnerLoginVisible &&
    codeFromIdp === null &&
     !isAuthenticated && !hasSeedSession &&
      pathname !== "/access/login" &&
      pathname !== "/access/password/recovery" &&
      pathname !== "/register" &&
      !routerState?.recover === true &&
      !routerState?.firstAccess === true) ||
      (registrationCompletedVisible !== null &&
         pathname !== "/access/login");
  };

  const shouldGoToAgreements = () => {
    return !agreedToConditions && isAuthenticated &&
      pathname !== "/agreements";
  };

  const shouldStayOnAgreements = () => {
    return !agreedToConditions && isAuthenticated && pathname === "/agreements";
  };

  const shouldGoToChangePassword = () => {
    return pathname === "/access/password/change";
  };

  const shouldGoToOnboarding = ()=>{
    return corporateHasOnBoarding && !completedOnBoarding &&
      isAuthenticated && agreedToConditions &&
      pathname !== "/access/login" &&
      pathname !== "/signup" &&
      pathname !== "/signup/profession" &&
      pathname !== "/signup/job" &&
      pathname !== "/signup/skills" &&
      pathname !== "/signup/capabilities" &&
      pathname !== "/signup/preferences" &&
      pathname !== "/signup/summary";
  };

  const shouldGoToPortal = () => {
    // Protezione: se c'è un fromUrl ma non ci sono iniziative caricate, aspetta
    // Questo previene il redirect al portal durante il caricamento delle iniziative
    if (fromUrl && isAuthenticated && initiatives.length === 0) {
      return false;
    }

    const result = isAuthenticated && !hasInitiative && agreedToConditions &&
      (completedOnBoarding || !corporateHasOnBoarding) &&
      !routerState?.firstAccess && pathname !== "/access/portal" && !hasValidFromUrl(fromUrl, initiatives);


    return result;
  };

  const shouldGoToHomepage = () => {
    const result = isAuthenticated && agreedToConditions && hasInitiative && !isSeedingSession &&
      !routerState?.firstAccess && pathname === "/";
    return result;
  };

  const postInitiativeId = useAuthStore(state => state.postInitiativeId);
  const setFromUrl = useAuthStore(state => state.setFromUrl);
  const setShowInitiativeEnrollmentWarning = useAuthStore(state => state.setShowInitiativeEnrollmentWarning);
  useEffect(() => {
    if ((fromUrl && !hasValidFromUrl(fromUrl, initiatives)) || hasInitiative){
      return;
    }

    // Seleziona automaticamente iniziativa in base all'url di provenienza
    if (fromUrl && initiatives){
      const initiativeId = getInitiativeIdFromUrl(fromUrl, initiatives);

      if (initiativeId) {
        postInitiativeId(initiativeId);
      }
      if (!initiativeId) {
        throw new Error(`L'iniziativa corrispondente all'url ${fromUrl} non è stata trovata`);
      }
    }
  }, [fromUrl, hasInitiative, initiatives, postInitiativeId]);

  // TODO: QUESTO VA NEL LOADER!!!!
  useEffect(() => {
    if (shouldRedirectToInitiative()) {
      setSpinnerLoginVisible(true);
      switchToInitiative();
      return;
    }
    if (shouldShowAuthError())  return;
    if (shouldHandleNavigateTo()) {
      handleNavigateTo();
      return;
    }
    if (shouldSeedSession()) {
      handleSeedSession();
      return;
    }
    if (shouldHandleErrorParams()) {
      handleErrorParams();
      return;
    }
    if (shouldHandleNavigateParam()) {
      handleNavigateParam();
      return;
    }
    if (shouldGoToLogin()) {
      // Dopo login sso arrivo qui da url di tipo https://openlearning-svil.digitedacademy.net/?code=cb0c5eae-5e7e-44e4-9aaa-6efab31cdb22&state=deep_link%3D1457%252FDA_SCORM
      let urlDeepLink = null;
      // se c'è solo basepath, o se il path comincia con /access non salvo fromUrl
      if (window.location.pathname.length > 1 && !window.location.pathname.startsWith("/access")) {
        urlDeepLink = window.location.href;
        setFromUrl(urlDeepLink);
      }
      navigate(`/access/login${searchParams.get("loginType") ? `?loginType=${searchParams.get("loginType")}` : ""}`, { state: { fromUrl: urlDeepLink } });
      return;
    }

    sessionStorage.removeItem("disable_sso_redirect");

    if (shouldGoToAgreements()) {
      setSpinnerLoginVisible(false);
      navigate("/agreements", { state: { first: true, isAuthenticated } });
      return;
    }
    if (shouldStayOnAgreements()) {
      setSpinnerLoginVisible(false);
      return;
    }
    if (shouldGoToOnboarding()) {
      setSpinnerLoginVisible(false);
      navigate("/signup", { state: { isAuthenticated } });
      return;
    }
    if (shouldGoToPortal()) {
      const isEnrolledToInitiative = hasValidFromUrl(fromUrl, initiatives);

      if(fromUrl && !isEnrolledToInitiative) {
        setShowInitiativeEnrollmentWarning(true);
      }

      setFromUrl(null);
      setSpinnerLoginVisible(true);
      navigate("/access/portal", { state: { first: true } });
      return;
    }
    if (shouldGoToHomepage()) {
      setNavbarItem(mainPageNavItem);
      const mainUrl = smartConfiguration?.visForyou === false ? "/esplora" : "/per-te";
      if (fromUrl && hasValidFromUrl(fromUrl, initiatives)) {
        setFromUrl(null);
        navigate(fromUrl);
      } else {
        navigate(mainUrl);
      }
      return;
    }
    if (!isAuthenticated && pathname === "/register") {
      setSpinnerLoginVisible(false);
    }
    if (shouldGoToChangePassword()){
      setSpinnerLoginVisible(false);
      return;
    }
  }, [
    isPO,
    hasInitiative,
    smartConfiguration,
    routerState,
    authError,
    pathname,
    mainPageNavItem,
    seedSessionId,
    authMode,
    searchParams,
    seedSession,
    errorParams,
    seedLogout,
    navigateParam,
    isAuthenticated,
    agreedToConditions,
    corporateHasOnBoarding,
    completedOnBoarding,
    navigateTo,
    spinnerLoginVisible,
    registrationCompletedVisible,
    fromUrl
  ]);


  const showMainLayout = isAuthenticated && hasInitiative
    && !pathname.includes("access")
    && !pathname.includes("academy")
    && !pathname.includes("signup")
    && !pathname.includes("agreements")&&
    !seedSessionId;

  /** lingua **/
  const organizationId = useAuthStore(state => state.userData?.organization_id);
  const initiativeId = useAuthStore(state => state.userData?.initiative_id);


  const { data: multiLanguageData } = useGetLanguages({ showMainLayout, initiativeId, organizationId });
  const languages = multiLanguageData?.languages;
  const isMultilanguage = multiLanguageData?.enable_multilanguage;

  const regex = new RegExp("(^| )activeLangCode=([^;]+)");
  const match = document.cookie.match(regex);
  const cookieLang = match && match[2];

  let langObj = null;
  if (cookieLang && multiLanguageData?.languages?.some(lang => lang.id === cookieLang)) {
    langObj = multiLanguageData?.languages.filter(lang => lang.id === cookieLang)[0];
  } else if (languageCookie && multiLanguageData?.languages?.some(lang => lang.id === languageCookie.id)) {
    langObj = languageCookie;
  } else if (multiLanguageData?.primary_language){
    langObj = multiLanguageData?.primary_language;
  }
  useGetTranslations({ language: langObj, setLanguage: setLanguage });

  /** lingua **/



  (authError || authNotice) && console.warn("Root.page", { authError, authNotice });

  const tutorialCompleted = getCookieByName("tutorial-completed");
  const tutorialForcedCompleted = getCookieByName(`tutorial-completed-${sessionToken}`);

  const RenderTutorial = useMemo(() => {
    if (showMainLayout) {
      const tutorialRenderElement = () => {
        if (corporateInfo?.tutorial_forced) {
          if (tutorialForcedCompleted) {
            return;
          }
          return <TutorialBox key={ `lang${language?.id}` }
            onMobileShowWidget={ (e: boolean) => setShowTutorialWidget(e) }
            onMobileShowUserMenu={ (e: boolean) => setShowTutorialUserMenu(e) }
            sessionToken={ sessionToken }
            origin={ window.location.pathname + window.location.search }
          />;
        } else {
          if (tutorialCompleted) {
            return;
          }
          if (corporateInfo?.tutorial) {
            return <TutorialBox key={ `lang${language?.id}` }
              onMobileShowWidget={ (e: boolean) => setShowTutorialWidget(e) }
              onMobileShowUserMenu={ (e: boolean) => setShowTutorialUserMenu(e) }
              origin={ window.location.pathname + window.location.search }
            />;
          }
        }
      };

      return <>
        { tutorialRenderElement() }
        { /* Mostra la Navbar solo se l'utente ha completato il login, ha accettato le condizioni, ha un'iniziativa e non è in fase di seeding session.
        In questo modo si evita la chiamata alla community nella Navbar se il flusso di login non è concluso. */ }
        { isAuthenticated && hasInitiative && !isSeedingSession && <Navbar /> }
      </>;
    } else {
      return <></>;
    }
  }, [showMainLayout, tutorialCompleted, corporateInfo?.tutorial, corporateInfo?.tutorial_forced, language, isAuthenticated, hasInitiative, isSeedingSession]);

  const handleKeyDown = (event:KeyboardEvent) => {
    if (event.ctrlKey && event.key === "u") {
      event.preventDefault();
      const elements:HTMLCollectionOf<Element> = document.getElementsByClassName("asw-menu-btn");
      if (elements.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const button:any = elements[0];
        if(button){
          button?.click();
        }
      }
    }
  };


  //SIENNA ACCESSIBILITY WIDGET
  useEffect(() => {
    const existingScript = document.querySelector<HTMLScriptElement>("script[src=\"/sienna.min.js\"]");

    if (showMainLayout && !existingScript && multiLanguageData?.primary_language) {
      const script = document.createElement("script");
      script.src = "/sienna.min.js";
      script.defer = true;
      document.body.appendChild(script);
    }

    if (showMainLayout) {
      window.addEventListener("keydown", handleKeyDown);
    }

    const observer = new MutationObserver(() => {
      const aswBtnSVG = document.querySelector(".asw-menu-btn svg");
      if (aswBtnSVG) {
        aswBtnSVG.setAttribute("aria-label", t("open_accessibility_menu"));
        aswBtnSVG.setAttribute("aria-hidden", "true");
        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showMainLayout, multiLanguageData]);

  const style = document.createElement("style");
  style.type = "text/css";

  style.textContent = `
  .asw-menu-btn:focus-visible::before {
    content: "";
    position: absolute;
    top: -12px;
    left: -12px;
    width: calc(100% + 24px);
    height: calc(100% + 24px);
    border: 5px solid ${theme.palette.text?.primary};
    border-radius: 0;
    pointer-events: none;
    box-sizing: border-box;
    z-index: 10;
  }
`;

  // 3. Append the style to <head>
  document.head.appendChild(style);

  return (
    <Fragment>
      <MultiTabBlocker open={ !isActive } onActivate={ activateTab } />
      <Helmet>
        <meta charSet="utf-8" />
        {
          environment !== "PROD" &&
            <meta name="robots" content="noindex" />
        }
        <title>{ t("app_name") } { pageTitle ? ` - ${capitalizeString(pageTitle)}` : "" }</title>
        <link rel="icon" type="image/png" href={ `${favicon}` } />
        <style type="text/css">{ `
          /*div#freshworks-container iframe#launcher-frame {
            max-width: 1440px !important;
            ${(excessWidth > 0) ? `padding: 0 ${Math.floor(excessWidth / 2)}px;` : ""}
            width: 100%;
             will-change: transform;
            transition: transform 0.1s ease-out;
          }*/

          #freshworks-container {
           width: 8rem !important;
            z-index: 9999 !important;
            position: relative !important;
            height: 5rem !important;
            width: 8rem !important;
            bottom: 0px !important;
            left: -1.5rem !important;
          }
          #freshworks-container iframe#launcher-frame {
            position: sticky !important;
            top: calc(100% - 6rem);
            height: 4rem !important;
            will-change: transform;
            transition: transform 0.1s ease-out;
          }

          #freshworks-frame-wrapper {
            z-index: 2147483001 !important;
            pointer-events: all;
          }

          iframe#widget-frame {
            z-index: 2147483001 !important;
            pointer-events: all !important;
          }
        ` }
        </style>
        <script type="text/javascript">{ `${analytics}` }</script>

      </Helmet>
      <div className={ "mask" }></div>
      <Stack minHeight="100vh" position="relative" display="flex" flexDirection="column">
        {
          localStorage.getItem("debug") ? <SessionRevoker /> : false
        }
        <SkipContentAccessibility/>
        <Header authenticated={ showMainLayout } showTutorialUserMenu={ showTutorialUserMenu } isMultilanguage={ isMultilanguage } languages={ languages } />
        { RenderTutorial }
        <main id="main-content" style={ { flex: 1 } }>
          <Outlet
            context={ {
              sectionUrl,
              setShowTutorialWidget,
              showTutorialWidget
            } satisfies OutletContext }
          />
        </main>
        {
          <Footer footerRef={ footerRef } isPreLogin={ ((!isAuthenticated || !hasInitiative) && pathname.includes("access")) || pathname.includes("register")  || pathname.includes("agreements") } />
        }
        { /* TODO creare componente apposito */ }
        <OverlaySupport footerHeightRem={ footerHeightRem } />
      </Stack>
      <LoginSpinner open={ spinnerLoginVisible } />

      <Modal
        description={ authError?.message ?? t("generic_error") }
        onClose={ () => {
          searchParams.delete("error");
          setSearchParams(searchParams);
          resetError();
        } }
        open={ Boolean(authError) && registrationCompletedVisible === null }
        title={ t("attention") }
      />
      <Modal
        description={ "Operazione avvenuta con successo" }
        onClose={ resetQrValidation }
        open={ qrCodeValidate }
        //TODO: DA TRADURRE ?
        title={ t("smart_tv_login") }
      />
      <Modal
        description={ authNotice ? t(authNotice) : "" }
        onClose={ resetNotice }
        open={ Boolean(!authError && authNotice) }
        title={ t("attention") }
      />
    </Fragment>
  );
}
